import {Grid, Tooltip, Typography, useTheme} from "@mui/material";
import * as React from "react"
import {FC, useContext, useMemo, useRef} from "react"
import {useTranslation} from "react-i18next";
import {IInvoiceDetails} from "../page/contract/ContractInterfaces";
import AgGridCustomTable from "../../../util/component/AgGridCustomTable";
import {ColDef, GridOptions} from "ag-grid-enterprise";
import {AgGridReact} from "ag-grid-react";
import EmptyState from "../../../util/component/EmptyState";
import {setSnackbar} from "../../../util/state/globalAction";
import {snackbarTypes} from "../../../util/state/globalVariables";
import {useContractActions} from "../service/useContractActions";
import {GlobalContext} from "../../../util/state/GlobalContextProvider";
import {useHasScope} from "../../../config/globalConfig";
import IconButton from "@mui/material/IconButton";
import {DescriptionOutlined, DescriptionRounded, RequestQuoteSharp} from "@mui/icons-material";
import SmDynamicActions from "./SmDynamicActions";
import {ICellRendererParams} from "ag-grid-community";
import ActionButtonWithTooltip, {ActionTypes} from "./ActionButtonWithTooltip";
import dayjs from "dayjs";
import {formatCurrency} from "./Utils";


const InvoiceTable:FC<{ data: IInvoiceDetails[], showTypography?: boolean }> =
    ({data, showTypography}) => {
    const {t} = useTranslation(['salesmarketing'], {keyPrefix: 'invoice'});
    const gridRef = useRef<AgGridReact>(null);
    const {dispatchGlobalData} = useContext(GlobalContext);
    const {downloadInvoice} = useContractActions();
    const hasScope = useHasScope;
    const theme = useTheme();
    const linkToInvoice = "/finance/invoices/";
    const linkToPayment = "/finance/payments/payment-request/";
    const SCOPE_INVOICE = "SCOPE_SM_FINANCE_RECEIVABLE_VIEW_DETAIL";
    const SCOPE_PAYMENT = "SCOPE_SM_FINANCE_PAYMENT_REQUEST_VIEW_DETAIL";

    const columns: ColDef[] = [
        {
            field: "invoiceNumber",
            headerName: t('invoiceNumber'),
            unSortIcon: true,
            tooltipValueGetter: (params: any) => (params?.value ? params.value : ""),
            sortable: true,
        },
        {
            field: 'invoiceDate',
            headerName: t('invoiceDate'),
            cellRenderer: (params: any) => params?.value ? dayjs(params.value).format("L LT") : "",
            unSortIcon: true,
            sortable: true,
        },
        {
            field: 'monthlyPeriod',
            headerName: t('monthlyPeriod'),
            unSortIcon: true,
            sortable: true,
        },
        {
            field: 'description',
            headerName: t('description'),
            unSortIcon: true,
            sortable: true,
            tooltipValueGetter: (params: any) => (params?.value ? params.value : "")
        },
        {
            field: 'amount',
            headerName: t('amount'),
            unSortIcon: true,
            sortable: true,
            valueFormatter: params => formatCurrency(params.value, 'EUR', 0.01)
        },
        {
            field: 'paymentStatus',
            headerName: t('paymentStatus'),
            cellRenderer: (params: any) => params?.value ? t('statuses.'+params.value) : "",
            unSortIcon: true,
            sortable: true,
        },
        {
            field: 'creditNote',
            headerName: t('creditNote'),
            unSortIcon: true,
            sortable: true,
        },
        {
            field: 'invoiceId',
            headerName: t('pdfId'),
            unSortIcon: true,
            cellRenderer: (params: ICellRendererParams<any>) => {
                return (
                    <ActionButtonWithTooltip type={ActionTypes.invoice}
                             callback={() => onDownloadInvoice(params.data.invoiceId)}
                             disabled={!params.data.invoiceId} auth={'SCOPE_SM_VOUCHER_DOWNLOAD_INVOICE'}/>

                );
            }
        },
        {
            field: "_actions", headerName: t('details'),
            minWidth: 100, maxWidth: 110,
            unSortIcon: false, sortable: false,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            hide: !hasScope(SCOPE_INVOICE),
            cellRendererSelector: (params) => {
                return {
                    component: () => (
                        <SmDynamicActions>
                            { hasScope(SCOPE_INVOICE) &&
                                <Tooltip arrow title={t('invoiceLink')}>
                                    <IconButton
                                        sx={{color: theme.palette.accent.main}}
                                        disabled={params.data.invoiceId === null}
                                        onClick={() => {
                                            window.open(linkToInvoice+ `${params.data.invoiceId}`, "_blank");
                                        }}
                                    >
                                        <DescriptionRounded/>
                                    </IconButton>
                                </Tooltip>
                            }
                            {hasScope(SCOPE_INVOICE) &&
                                <Tooltip arrow title={t('creditNoteLink')}>
                                    <IconButton
                                        sx={{color: theme.palette.accent.main}}
                                        disabled={params.data.creditNoteId === null}
                                        onClick={() => {
                                            window.open(linkToInvoice+ `${params.data.creditNoteId}`, "_blank");
                                        }}
                                    >
                                        <DescriptionOutlined/>
                                    </IconButton>
                                </Tooltip>
                            }
                            { hasScope(SCOPE_PAYMENT) &&
                                <Tooltip arrow title={t('paymentLink')}>
                                    <IconButton
                                        sx={{color: theme.palette.accent.main}}
                                        disabled={params.data.paymentId === null}
                                        onClick={() => {
                                            window.open(linkToPayment+ `${params.data.paymentId}`, "_blank");
                                        }}
                                    >
                                        <RequestQuoteSharp/>
                                    </IconButton>
                                </Tooltip>
                            }
                        </SmDynamicActions>
                    ),
                };
            },
            suppressFiltersToolPanel: true,
        },
    ]

    const gridOptions: GridOptions<IInvoiceDetails> = {
        columnDefs: columns,
        suppressRowClickSelection: true,
        pagination: true,
        paginationPageSize: 10,
        cacheBlockSize: 10,
        defaultColDef: {
            flex: 1,
            resizable: true,
            wrapHeaderText: true
        },
        animateRows: true,
        detailRowHeight: 200,
    };

    const rowData = useMemo<IInvoiceDetails[]>(() => (
        data ?? []
    ), [data]);

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const noRowsOverlayComponentParams = useMemo<any>(() => {
        return {
            title: t(" "),
            content: t(" ")
        };
    }, []);


    const onDownloadInvoice = async (invoiceId: number) => {
        try {
            const {result} = await downloadInvoice(invoiceId);
            if (result) {
                let pdfWindow = window.open("");
                pdfWindow!.document.write(`<iframe width='100%' height='100%' src='data:application/pdf;base64, ${encodeURI(result)}'></iframe>`);
            }
        } catch (err) {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("downloadError"),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });

        }
    }

    return <>
        {showTypography &&  <Grid item>
                <Typography>{t('title')}</Typography>
            </Grid>
        }
        <Grid container>
            <Grid item xs={12} sx={{padding: "16px"}}>
                <AgGridCustomTable gridOptions={gridOptions} gridRef={gridRef} rowData={rowData}
                                   noRowsOverlayComponent={noRowsOverlayComponent}
                                   noRowsOverlayComponentParams={noRowsOverlayComponentParams}
                />
            </Grid>
        </Grid>
    </> 
}



export default InvoiceTable;